import { useMutation, useQuery } from '@tanstack/react-query';
import { createWorkingUnit, getWorkingUnits, updateWorkingUnit } from '../../services/workingUnit';
import { QueryKey } from '../../enums/QueryKey';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { queryClient } from '../../services/queryClient';
import { useUpdateRecord } from './crudOperations';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import { WorkingUnit } from '../../types/WorkingUnit.interface';

export const ALL_WORKING_UNITS_OPTION = {
    id: -1,
    worktypeId: -1,
    channelId: -1,
    taskId: -1,
    worktype: {
        id: -1,
        worktype: 'Alle Working Units',
    },
    channel: {
        id: -1,
        name: '',
    },
    task: {
        id: -1,
        name: '',
    },
};

export function useWorkingUnits({
    location,
    hasMatchingOfferedCluster = false,
    prependAllOption = false,
    withHistoricVolume = false,
}: {
    location?: number;
    hasMatchingOfferedCluster?: boolean;
    prependAllOption?: boolean;
    withHistoricVolume?: boolean;
}): [WorkingUnit[], boolean] {
    const { data: workingUnits, isLoading } = useQuery({
        queryKey: [QueryKey.WorkingUnits, { ...(location && { location }), hasMatchingOfferedCluster, withHistoricVolume }],
        queryFn: () => getWorkingUnits({ location, hasMatchingOfferedCluster, withHistoricVolume }),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    if (workingUnits && workingUnits.length && prependAllOption) {
        return [[ALL_WORKING_UNITS_OPTION, ...(workingUnits || [])], isLoading];
    }

    return [workingUnits || [], isLoading];
}

export function useCreateWorkingUnit<TData, TError extends Error>({ onSettled }: UseMutationOptions<TData, TError>) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<TData, TError>({
        mutationFn: createWorkingUnit,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.WorkingUnits]);
            dispatchSuccessfullySavedRecord('Working Unit');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
        onSettled,
    });
}

export function useUpdateWorkingUnit({ onSettled }: UseMutationOptions) {
    return useUpdateRecord({
        updateRecordFn: updateWorkingUnit,
        fetchRecordsQueryKey: [QueryKey.WorkingUnits],
        recordName: 'Working Unit',
        onSettled,
    });
}
